import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { to: '/', label: 'Home', icon: 'ri-home-4-line' },
  { to: '/services', label: 'Services', icon: 'ri-tools-line' },
  { to: '/savings-calculator', label: 'Savings Calculator', icon: 'ri-calculator-line' },
  { to: '/testimonials', label: 'Testimonials', icon: 'ri-chat-quote-line' },
  { to: '/faq', label: 'FAQ', icon: 'ri-question-line' },
  { to: '/protection-plans', label: 'Protection Plans', icon: 'ri-shield-check-line' },
  { to: '/service-areas', label: 'Areas', icon: 'ri-map-pin-line' },
];

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const touchStartX = useRef(0);
  const touchCurrentX = useRef(0);
  const menuPanelRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Close menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const closeMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // Swipe-to-close gesture handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = e.touches[0].clientX;
    isDragging.current = true;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    touchCurrentX.current = e.touches[0].clientX;
    const diff = touchCurrentX.current - touchStartX.current;
    // Only allow swiping right (to close)
    if (diff > 0 && menuPanelRef.current) {
      menuPanelRef.current.style.transform = `translateX(${diff}px)`;
      menuPanelRef.current.style.transition = 'none';
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const diff = touchCurrentX.current - touchStartX.current;
    if (menuPanelRef.current) {
      menuPanelRef.current.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      if (diff > 100) {
        // Swipe far enough — close
        menuPanelRef.current.style.transform = 'translateX(100%)';
        setTimeout(closeMenu, 300);
      } else {
        // Snap back
        menuPanelRef.current.style.transform = 'translateX(0)';
      }
    }
  }, [closeMenu]);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="w-full px-6">
          <div className="flex items-center justify-between py-0">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/73df10f1d2f13e308059e9f42ef7d721/190813f7ec57e49decbb81ad219363d3.jpeg"
                alt="United States Home Solutions"
                className="transition-all duration-300 h-[64px] lg:h-[100px] w-auto object-contain bg-transparent"
                style={{ mixBlendMode: 'multiply' }}
              />
            </Link>

            {/* Desktop nav links */}
            <div className="hidden lg:flex items-center justify-center flex-1 mx-6">
              <div className="flex items-center gap-6 xl:gap-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`text-[13px] xl:text-[14px] font-semibold tracking-wide transition-colors whitespace-nowrap ${
                      location.pathname === link.to
                        ? 'text-brand-red'
                        : 'text-gray-700 hover:text-brand-red'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Right side - phone + CTA */}
            <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
              <a
                href="tel:+18005551234"
                className="text-sm font-medium whitespace-nowrap text-brand-navy"
              >
                (800) 555-1234
              </a>
              <a
                href="#contact"
                className="bg-brand-red text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-brand-red-dark transition-all duration-300 hover:scale-105 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line"></i>
                Free Quote
              </a>
            </div>

            {/* Mobile menu toggle — animated hamburger */}
            <button
              className="lg:hidden w-11 h-11 flex items-center justify-center rounded-lg active:bg-gray-100 transition-colors cursor-pointer relative"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <div className="w-6 h-5 relative flex flex-col justify-between">
                <span
                  className={`block h-0.5 w-6 bg-gray-900 rounded-full transition-all duration-300 origin-center ${
                    isMobileMenuOpen ? 'rotate-45 translate-y-[9px]' : ''
                  }`}
                />
                <span
                  className={`block h-0.5 w-6 bg-gray-900 rounded-full transition-all duration-300 ${
                    isMobileMenuOpen ? 'opacity-0 scale-x-0' : ''
                  }`}
                />
                <span
                  className={`block h-0.5 w-6 bg-gray-900 rounded-full transition-all duration-300 origin-center ${
                    isMobileMenuOpen ? '-rotate-45 -translate-y-[9px]' : ''
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay + Slide-in Panel */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden transition-visibility ${
          isMobileMenuOpen ? 'visible' : 'invisible delay-300'
        }`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={closeMenu}
        />

        {/* Slide-in panel from right */}
        <div
          ref={menuPanelRef}
          className={`absolute top-0 right-0 h-full w-[85%] max-w-[360px] bg-white shadow-2xl transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Panel header */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
            <span className="text-lg font-bold text-gray-900">Menu</span>
            <button
              onClick={closeMenu}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
              aria-label="Close menu"
            >
              <i className="ri-close-line text-xl text-gray-700"></i>
            </button>
          </div>

          {/* Scrollable nav links */}
          <div className="overflow-y-auto h-[calc(100%-180px)] py-3">
            {navLinks.map((link, index) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={closeMenu}
                  className={`flex items-center gap-4 mx-3 px-4 py-4 rounded-xl transition-all duration-200 active:scale-[0.98] cursor-pointer ${
                    isActive
                      ? 'bg-brand-red-light text-brand-red'
                      : 'text-gray-700 active:bg-gray-50'
                  }`}
                  style={{
                    animationDelay: `${index * 50}ms`,
                    animation: isMobileMenuOpen
                      ? `slideInRight 0.3s ease forwards ${index * 50}ms`
                      : 'none',
                    opacity: isMobileMenuOpen ? 0 : 1,
                  }}
                >
                  <div
                    className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                      isActive ? 'bg-brand-red text-white' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    <i className={`${link.icon} text-lg`}></i>
                  </div>
                  <span className="text-[15px] font-semibold">{link.label}</span>
                  {isActive && (
                    <div className="ml-auto w-2 h-2 rounded-full bg-brand-red" />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Bottom actions */}
          <div className="absolute bottom-0 left-0 right-0 p-5 border-t border-gray-100 bg-white space-y-3">
            <a
              href="tel:+18005551234"
              className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-brand-navy text-white font-semibold text-[15px] active:scale-[0.98] transition-transform cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line text-lg"></i>
              (800) 555-1234
            </a>
            <a
              href="#contact"
              onClick={closeMenu}
              className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-brand-red text-white font-semibold text-[15px] active:scale-[0.98] transition-transform cursor-pointer whitespace-nowrap"
            >
              Get Free Quote
              <i className="ri-arrow-right-line text-lg"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Keyframe animation for staggered link entrance */}
      <style>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(24px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
}
